import { calculateAge } from '@/utils/pediatrics';
import type { Member } from '../types';

/**
 * Sanitize an array of members by fixing known data inconsistencies.
 *
 * Applied fixes (in order):
 * 1. Backfill missing `id` with `crypto.randomUUID()`
 * 2. Ensure exactly one member has `isPrimary === true`
 *    - If any adult (age >= 18) exists: keep an existing adult primary, or
 *      assign the oldest adult as primary
 *    - If no adults exist: first child in the array becomes primary
 *
 * @param members - Array of members to sanitize
 * @returns A new array with all fixes applied
 */
export function sanitizeMembers(members: Member[]): Member[] {
  if (members.length === 0) return [];

  // 1. Backfill missing ids
  let result = members.map(m => (m.id ? m : { ...m, id: crypto.randomUUID() }));

  // 2. Normalize primary
  const adults = result.filter(m => calculateAge(m.date_of_birth) >= 18);

  if (adults.length > 0) {
    const adultPrimary = adults.find(m => m.isPrimary);

    if (adultPrimary) {
      // An adult is already primary — clear any other primaries
      result = result.map(m => ({
        ...m,
        isPrimary: m === adultPrimary,
      }));
    } else {
      // No adult is primary (child is primary or no one is) — oldest adult wins
      const oldestAdult = adults.reduce((oldest, m) =>
        m.date_of_birth < oldest.date_of_birth ? m : oldest
      );
      result = result.map(m => ({
        ...m,
        isPrimary: m === oldestAdult,
      }));
    }
  } else {
    // No adults — first child becomes primary
    result = result.map((m, i) => ({
      ...m,
      isPrimary: i === 0,
    }));
  }

  return result;
}
